import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  buildUnknownRefreshStatusPayload,
  safeBuildRefreshActionState,
} from '../utils/providerRefreshAction';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

function normalizeProviderKey(provider) {
  return provider?.toLowerCase().replace(/\s/g, '').replace(/-/g, '').replace(/_/g, '') || '';
}

/**
 * Fetch provider refresh metadata and derive shared button action state.
 * Refresh-status failures never throw; they fall back to a safe unknown state.
 */
export function useProviderRefreshAction(provider, { enabled = true, hasSavedReport = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled && provider));
  const [error, setError] = useState(null);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [statusUnavailable, setStatusUnavailable] = useState(false);
  const autoFetchedRef = useRef(false);
  const providerKey = normalizeProviderKey(provider);

  const applyFallbackState = useCallback((credsExist = false) => {
    setData(buildUnknownRefreshStatusPayload({
      providerKey,
      hasCredentials: credsExist,
    }));
    setStatusUnavailable(true);
  }, [providerKey]);

  const refetch = useCallback(async () => {
    if (!provider || !enabled) {
      setLoading(false);
      return null;
    }

    const token = localStorage.getItem('fundelite_token');
    if (!token) {
      setLoading(false);
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const [refreshRes, credRes] = await Promise.all([
        axios.get(`${API}/credit-report/refresh-status/${providerKey}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/credit-report/credentials`, {
          params: { provider: providerKey },
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: null })),
      ]);

      let credsExist = false;
      const credData = credRes?.data;
      if (credData?.credentials?.length > 0) {
        credsExist = credData.credentials.some((c) => c.provider === providerKey);
      } else if (credData?.username_masked && credData?.provider === providerKey) {
        credsExist = true;
      }

      setHasCredentials(credsExist);

      const refreshData = refreshRes?.data?.success ? refreshRes.data : null;
      if (refreshData) {
        setData(refreshData);
        setStatusUnavailable(false);
        return refreshData;
      }

      applyFallbackState(credsExist);
      return null;
    } catch (err) {
      setError(err.message || 'Failed to load refresh status');
      applyFallbackState(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, [provider, providerKey, enabled, applyFallbackState]);

  useEffect(() => {
    autoFetchedRef.current = false;
  }, [providerKey, enabled]);

  useEffect(() => {
    if (!enabled || !provider) {
      setLoading(false);
      return undefined;
    }
    if (autoFetchedRef.current) {
      return undefined;
    }
    autoFetchedRef.current = true;
    refetch();
    return undefined;
  }, [enabled, provider, providerKey, refetch]);

  const actionState = safeBuildRefreshActionState(data, {
    hasCredentials,
    hasSavedReport: hasSavedReport || hasCredentials,
  });

  return {
    data,
    loading,
    error,
    hasCredentials,
    statusUnavailable,
    actionState,
    refetch,
  };
}

export default useProviderRefreshAction;
